import axios from "axios";
import ErrorMessageService from "@/services/ErrorMessageService";
import store from "@/store/store";
import Restaurant from "@/domain/restaurant/Restaurant";
import WorkingHours from "@/domain/restaurant/WorkingHours";
import ClosedPeriod from "@/domain/restaurant/ClosedPeriod";
import Room from "@/domain/restaurant/Room";
import Table from "@/domain/restaurant/Table";
import Booking from "@/domain/booking/Booking";
import AuthDto from "@/domain/user/AuthDto";
import User from "@/domain/user/User";
import Stats from "@/domain/Stats";
import TimeService from "@/services/TimeService";
import Notice from "@/domain/restaurant/Notice";

const httpClient = axios.create();
httpClient.defaults.timeout = 15000;
httpClient.defaults.baseURL = process.env.VUE_APP_ROOT_API;
httpClient.defaults.headers.post["Content-Type"] = "application/json";
httpClient.defaults.timeoutErrorMessage = "timeout";
httpClient.interceptors.request.use(
  async config => {
    if (await store.getters.isLoggedIn()) {
      config.headers.Authorization = "";
      const token = await store.getters.getToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

export default class RemoteServices {
  static CAPTCHA_REQUEST_PARAM: string = "g-recaptcha-response";

  static errorMessage(error: any): string {
    return ErrorMessageService.errorMessage(error); // TODO make proper messages
  }

  /*
  ######################################################################################################################

  ################################################     Stats     #######################################################

  ######################################################################################################################
    */

  static async getStatsByWorkingHours(
    workingHours: WorkingHours,
    dateString: string
  ): Promise<Stats> {
    return await httpClient
      .get(
        `/stats/working-hours/${workingHours.workingHoursId}/date/${dateString}`
      )
      .then(response => {
        return new Stats(response.data);
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  /*
  ######################################################################################################################
  
  ################################################     User     ########################################################
  
  ######################################################################################################################
   */
  static async getUserInformation(): Promise<AuthDto> {
    return await httpClient
      .get("/auth/user")
      .then(response => {
        return new AuthDto(response.data);
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async getUsers(): Promise<User[]> {
    return httpClient
      .get("/users")
      .then(response => {
        const list: User[] = [];
        response.data.forEach((b: User) => list.push(new User(b)));
        return list;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async createUser(user: User): Promise<User> {
    return httpClient
      .put("/users/create", user)
      .then(response => {
        return new User(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateUserProperties(user: User): Promise<User> {
    return httpClient
      .put(`/users/${user.userId}/update/properties`, user)
      .then(response => {
        return new User(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateUserPreferences(user: User): Promise<User> {
    return httpClient
      .put(`/users/${user.userId}/update/preferences`, user)
      .then(response => {
        return new User(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async deleteUser(user: User) {
    return httpClient
      .delete(`/users/delete/${user.userId}`)
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async resetUserPassword(user: User) {
    return httpClient
      .put(`/users/reset-password/${user.userId}`)
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async confirmEmail(user: User) {
    return httpClient
      .put(`/users/confirm-email/${user.userId}`)
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  /*
  ######################################################################################################################
  
  ################################################  Restaurant  ########################################################
  
  ######################################################################################################################
   */
  static async createRestaurant(restaurant: Restaurant): Promise<Restaurant> {
    return httpClient
      .put("/restaurant/create", restaurant)
      .then(response => {
        return new Restaurant(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async editRestaurant(restaurant: Restaurant): Promise<Restaurant> {
    return httpClient
      .put(`restaurant/${restaurant.restaurantId}/props/edit`, restaurant)
      .then(response => {
        return new Restaurant(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async deleteRestaurant(
    restaurantId: number,
    cancellationMessage: string
  ) {
    return httpClient
      .delete(`/restaurant/delete/${restaurantId}`, {
        data: { message: cancellationMessage }
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  /*
  ######################################################################################################################
 
  ############################################### WORKING HOURS ########################################################
 
  ######################################################################################################################
  */

  static async createWorkingHours(
    workingHours: WorkingHours,
    restaurantId: number
  ): Promise<WorkingHours> {
    return httpClient
      .put(`/restaurant/${restaurantId}/working-hours/create`, workingHours)
      .then(response => {
        return new WorkingHours(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateWorkingHours(
    workingHours: WorkingHours,
    restaurantId: number
  ): Promise<WorkingHours> {
    return httpClient
      .put(`/restaurant/${restaurantId}/working-hours/edit`, workingHours)
      .then(response => {
        return new WorkingHours(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async deleteWorkingHours(
    workingHoursId: number,
    restaurantId: number,
    cancellationMessage: string
  ) {
    return httpClient
      .delete(
        `/restaurant/${restaurantId}/working-hours/${workingHoursId}/delete`,
        {
          data: { message: cancellationMessage }
        }
      )
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  /*
  ######################################################################################################################

  ###############################################    HOLIDAY    ########################################################

  ######################################################################################################################
  */

  static async createHoliday(
    holiday: ClosedPeriod,
    restaurantId: number
  ): Promise<ClosedPeriod> {
    return httpClient
      .put(`/restaurant/${restaurantId}/holidays/create`, holiday)
      .then(response => {
        return new ClosedPeriod(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateHoliday(
    holiday: ClosedPeriod,
    restaurantId: number
  ): Promise<ClosedPeriod> {
    return httpClient
      .put(`/restaurant/${restaurantId}/holidays/edit`, holiday)
      .then(response => {
        return new ClosedPeriod(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async deleteHoliday(
    holidayId: number,
    restaurantId: number,
    cancellationMessage: string
  ) {
    return httpClient
      .delete(`/restaurant/${restaurantId}/holidays/${holidayId}/delete`, {
        data: { message: cancellationMessage }
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async addSpecialOpenDate(
    dateString: string,
    restaurantId: number
  ): Promise<string> {
    return httpClient
      .put(`/restaurant/${restaurantId}/open-date/add/${dateString}`)
      .then(response => {
        return response.data;
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async removeSpecialOpenDate(
    dateString: string,
    restaurantId: number
  ): Promise<string> {
    return httpClient
      .delete(`/restaurant/${restaurantId}/open-date/remove/${dateString}`)
      .then(response => {
        return response.data;
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  /*
  ######################################################################################################################

  #############################################      ROOM       #######################################################

  ######################################################################################################################
  */

  static async createRoom(room: Room, restaurantId: number): Promise<Room> {
    return httpClient
      .put(`/rooms/restaurant/${restaurantId}/create`, room)
      .then(response => {
        return new Room(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async deleteRoom(roomId: number) {
    return httpClient.delete(`/rooms/${roomId}/delete`).catch(async error => {
      throw Error(await ErrorMessageService.errorMessage(error));
    });
  }

  static async updateRoomProperties(room: Room): Promise<Room> {
    return httpClient
      .put(`/rooms/${room.roomId}/edit`, room)
      .then(response => {
        return new Room(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  /*
  ######################################################################################################################

  ##########################################  ROOM CLOSED PERIOD  ######################################################

  ######################################################################################################################
  */
  static async createRoomClosedPeriod(
    roomClosedPeriod: ClosedPeriod,
    roomId: number
  ): Promise<ClosedPeriod> {
    return httpClient
      .put(`/rooms/${roomId}/closed-periods/create`, roomClosedPeriod)
      .then(response => {
        return new ClosedPeriod(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateRoomClosedPeriod(
    roomClosedPeriod: ClosedPeriod
  ): Promise<ClosedPeriod> {
    return httpClient
      .put(
        `/closed-periods/${roomClosedPeriod.closedPeriodId}/edit`,
        roomClosedPeriod
      )
      .then(response => {
        return new ClosedPeriod(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async deleteRoomClosedPeriod(roomClosedPeriodId: number) {
    return httpClient
      .delete(`rooms/closed-periods/${roomClosedPeriodId}/delete`)
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  /*
  ######################################################################################################################

  #############################################      TABLE       #######################################################

  ######################################################################################################################
  */

  static async createTable(table: Table): Promise<Table> {
    return httpClient
      .put(`/tables/create`, table)
      .then(response => {
        return new Table(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async createJoinedTable(table: Table): Promise<Table> {
    return httpClient
      .put(`/tables/create/joined`, table)
      .then(response => {
        return new Table(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateTable(table: Table): Promise<Table> {
    return httpClient
      .put(`/tables/${table.tableId}/edit`, table)
      .then(response => {
        return new Table(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateJoinedTable(table: Table): Promise<Table> {
    return httpClient
      .put(`/tables/${table.tableId}/edit/joined`, table)
      .then(response => {
        return new Table(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async deleteTable(tableId: number) {
    return httpClient.delete(`/tables/${tableId}/delete`).catch(async error => {
      throw Error(await ErrorMessageService.errorMessage(error));
    });
  }

  /*
######################################################################################################################

#############################################     BOOKINGS     #######################################################

######################################################################################################################
*/
  static async getBookingsByDateAtWorkingHours(
    workingHours: WorkingHours,
    date: string
  ) {
    return httpClient
      .get(`bookings/working-hours/${workingHours.workingHoursId}/date/${date}`)
      .then(response => {
        const list: Booking[] = [];
        response.data.forEach((b: Booking) => list.push(new Booking(b)));
        return list;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async getBookingsByDate(restaurant: Restaurant, date: string) {
    return httpClient
      .get(`bookings/restaurant/${restaurant.restaurantId}/date/${date}`)
      .then(response => {
        const list: Booking[] = [];
        response.data.forEach((b: Booking) => list.push(new Booking(b)));
        return list;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async createBooking(booking: Booking): Promise<Booking> {
    return httpClient
      .put(`bookings/restaurant/${booking.restaurantId}/create`, booking)
      .then(response => {
        return new Booking(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateBookingCustomerDetails(
    booking: Booking
  ): Promise<Booking> {
    return httpClient
      .put(`bookings/${booking.bookingId}/update/customer`, booking)
      .then(response => {
        return new Booking(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateBookingProperties(booking: Booking): Promise<Booking> {
    return httpClient
      .put(`bookings/${booking.bookingId}/update/properties`, booking)
      .then(response => {
        return new Booking(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async updateBookingStatus(
    bookingId: number,
    status: string,
    message: string | null
  ): Promise<Booking> {
    return httpClient
      .put(`bookings/${bookingId}/update/status/${status}`, message)
      .then(response => {
        return new Booking(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async markBookingAsSeated(bookingId: number): Promise<Booking> {
    return await RemoteServices.updateBookingStatus(bookingId, "SEATED", null);
  }

  static async markBookingAsComplete(bookingId: number): Promise<Booking> {
    return await RemoteServices.updateBookingStatus(
      bookingId,
      "COMPLETE",
      null
    );
  }

  static async getAvailableTimesByWorkingHours(
    date: string,
    numberOfPeople: number,
    workingHours: WorkingHours,
    roomId: number,
    bookingToMoveId: number
  ) {
    const params = new URLSearchParams();
    if (roomId != null) {
      params.append("room", String(roomId));
      /* Optionally sends the room Id */
    }
    if (bookingToMoveId != null) {
      params.append("booking", String(bookingToMoveId));
      /* Optionally sends the room Id */
    }
    return httpClient
      .get(
        `bookings/availability/${date}/${numberOfPeople}/working-hours/${workingHours.workingHoursId}`,
        { params: params }
      )
      .then(response => {
        const list: string[] = [];
        response.data.forEach((s: string) => list.push(s));
        return list;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async doManualOverride(
    workingHours: WorkingHours,
    bookings: Booking[]
  ): Promise<Booking[]> {
    return httpClient
      .put(
        `bookings/manual-override/working-hours/${workingHours.workingHoursId}`,
        bookings
      )
      .then(response => {
        const list: Booking[] = [];
        response.data.forEach((b: Booking) => list.push(new Booking(b)));
        return list;
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async cancelManualOverride(
    workingHours: WorkingHours,
    bookings: Booking[]
  ): Promise<Booking[]> {
    return httpClient
      .put(
        `bookings/manual-override/cancel/working-hours/${workingHours.workingHoursId}`,
        bookings
      )
      .then(response => {
        const list: Booking[] = [];
        response.data.forEach((b: Booking) => list.push(new Booking(b)));
        return list;
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async deleteBookingCustomerDataGDPR(
    booking: Booking
  ): Promise<Booking> {
    return httpClient
      .put(`bookings/gdpr/${booking.bookingId}`)
      .then(response => {
        return new Booking(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async addBookingToWaitingList(booking: Booking): Promise<Booking> {
    return httpClient
      .put(
        `bookings/restaurant/${booking.restaurantId}/waiting-list/add`,
        booking
      )
      .then(response => {
        return new Booking(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async createBookingFromWaitingList(
    booking: Booking
  ): Promise<Booking> {
    return httpClient
      .put(
        `bookings/restaurant/${booking.restaurantId}/create/waiting-list`,
        booking
      )
      .then(response => {
        return new Booking(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async getDatesWithBookingsInMonth(
    date: Date,
    restaurant: Restaurant
  ): Promise<string[]> {
    return httpClient
      .get(
        `bookings/restaurant/${
          restaurant.restaurantId
        }/dates/${TimeService.getYear(date)}/${TimeService.getMonth(date)}`
      )
      .then(response => {
        return response.data;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async getPotentialTablesToSwapBooking(
    booking: Booking
  ): Promise<Table[]> {
    return httpClient
      .get(`bookings/change-table/${booking.bookingId}`)
      .then(response => {
        const list: Table[] = [];
        response.data.forEach((t: Table) => list.push(new Table(t)));
        return list;
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async quickChangeBookingTable(booking: Booking): Promise<Booking> {
    return httpClient
      .put(`bookings/change-table/${booking.bookingId}`, booking)
      .then(response => {
        return new Booking(response.data);
      })
      .catch(async error => {
        throw Error(await ErrorMessageService.errorMessage(error));
      });
  }

  static async searchBookingsCustomerName(
    restaurant: Restaurant,
    customerName: string,
    page: number,
    pageSize: number
  ) {
    const params = new URLSearchParams();
    if (page != null) {
      params.append("page", String(page));
      /* Optionally sends the pageNumber */
    }
    if (pageSize != null) {
      params.append("size", String(pageSize));
    }
    return httpClient
      .get(
        `bookings/restaurant/${restaurant.restaurantId}/search/name/${customerName}`,
        { params: params }
      )
      .then(response => {
        const list: Booking[] = [];
        response.data.forEach((b: Booking) => list.push(new Booking(b)));
        return list;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async searchBookingsPhoneNumber(
    restaurant: Restaurant,
    phoneNumber: string,
    page: number,
    pageSize: number
  ) {
    const params = new URLSearchParams();
    if (page != null) {
      params.append("page", String(page));
      /* Optionally sends the pageNumber */
    }
    if (pageSize != null) {
      params.append("size", String(pageSize));
    }
    return httpClient
      .get(
        `bookings/restaurant/${restaurant.restaurantId}/search/phone/${phoneNumber}`,
        { params: params }
      )
      .then(response => {
        const list: Booking[] = [];
        response.data.forEach((b: Booking) => list.push(new Booking(b)));
        return list;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  /*
######################################################################################################################

#############################################      NOTICE     #######################################################

######################################################################################################################
*/

  static async getNoticesByDate(restaurant: Restaurant, dateString: string) {
    return httpClient
      .get(`restaurant/${restaurant.restaurantId}/notice/date/${dateString}`)
      .then(response => {
        const list: Notice[] = [];
        response.data.forEach((n: Notice) => list.push(new Notice(n)));
        return list;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async createNotice(restaurant: Restaurant, notice: Notice) {
    return httpClient
      .put(`restaurant/${restaurant.restaurantId}/notice/create`, notice)
      .then(response => {
        return new Notice(response.data);
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async updateNoticeMessage(restaurant: Restaurant, notice: Notice) {
    return httpClient
      .put(`restaurant/${restaurant.restaurantId}/notice/update`, notice)
      .then(response => {
        return new Notice(response.data);
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async deleteNotice(restaurant: Restaurant, notice: Notice) {
    return httpClient
      .delete(
        `restaurant/${restaurant.restaurantId}/notice/delete/${notice.noticeId}`
      )
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  /*
######################################################################################################################

#############################################      PUBLIC     #######################################################

######################################################################################################################
*/

  static async getRestaurantPublic(restId: number) {
    return httpClient
      .get(`public/restaurant/${restId}`)
      .then(response => {
        return new Restaurant(response.data);
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async getRestaurantBlockedDatesByMonth(
    restId: number,
    dateString: string
  ) {
    return httpClient
      .get(`public/restaurant/${restId}/dates/${dateString}`)
      .then(response => {
        const list: string[] = [];
        response.data.forEach((s: string) => list.push(s));
        return list;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async getAvailableTimesByDatePublic(
    roomId: number,
    nPeople: number,
    dateString: string
  ): Promise<string[]> {
    return httpClient
      .get(`public/bookings/availability/${roomId}/${nPeople}/${dateString}`)
      .then(response => {
        const list: string[] = [];
        response.data.forEach((s: string) => list.push(s));
        return list;
      })
      .catch(async error => {
        throw Error(ErrorMessageService.errorMessage(error));
      });
  }

  static async createOnlineBooking(
    restId: number,
    booking: Booking,
    captchaToken: string
  ) {
    const params = new URLSearchParams();
    if (captchaToken != null)
      params.append(this.CAPTCHA_REQUEST_PARAM, captchaToken);

    return httpClient
      .put(`public/bookings/create/${restId}`, booking, { params: params })
      .then(response => {
        return new Booking(response.data);
      });
  }

  static async getBookingPublic(bookingId: number, bookingKey: string): Promise<Booking> {
    return httpClient
        .get(`public/bookings/get/${bookingId}/${bookingKey}`)
        .then(response => {
          return new Booking(response.data);
        })
        .catch(async error => {
          throw Error(ErrorMessageService.errorMessage(error));
        });
  }


  static async cancelBookingPublic(bookingId: number, bookingKey: string, captchaToken: string): Promise<Booking> {
    const params = new URLSearchParams();
    if (captchaToken != null)
      params.append(this.CAPTCHA_REQUEST_PARAM, captchaToken);
    return httpClient
        .put(`public/bookings/cancel/${bookingId}/${bookingKey}`, null, { params: params })
        .then(response => {
          return new Booking(response.data);
        })
        .catch(async error => {
          throw Error(ErrorMessageService.errorMessage(error));
        });
  }
}
